// @file Dashboard store
import { trackEvent, trackPageview } from '@@/bits/analytics'
import { isActiveFilter } from '@@/bits/collections_helper'
import {
  dashViewUrl,
  getDashViewFromUrlParams,
  getDashViewUrlFromStorage,
  saveDashViewUrlIntoStorage,
} from '@@/bits/dash_helper'
import device from '@@/bits/device'
import { WAVING_HAND_EMOJI } from '@@/bits/emoji'
import { captureFetchException, setScope } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import {
  buildUrlFromPath,
  currentHostWithProtocol,
  getAndClearSearchParam,
  getSearchParam,
  navigateTo,
  transformUrl,
} from '@@/bits/location'
import { buildUserIdCacheKey, fetchCachedQuery } from '@@/bits/query_client'
import { isBreakoutUrlFormat, isSubmissionRequestUrlFormat, isValidWallUrlFormat } from '@@/bits/url'
import {
  GoogleAppLicensingSettings as GoogleAppLicenseApi,
  PersonalToLibraryTransferWalls as PersonalToLibraryTransferWallsApi,
} from '@@/dashboard/padlet_api'
import {
  FetchJsonStatus,
  LibraryMembershipRole,
  LibraryType,
  SmartSuggestionsExperimentVariant,
  SnackbarNotificationType,
} from '@@/enums'
import { useClassInformationStore } from '@@/pinia/class_information_store'
import { useDashAccountsStore } from '@@/pinia/dash_accounts_store'
import { useDashAiMakeSuggestionsStore } from '@@/pinia/dash_ai_make_suggestions_store'
import { useDashCollectionsStore } from '@@/pinia/dash_collections_store'
import { useDashGalleryStore } from '@@/pinia/dash_gallery_store'
import { useDashLayoutPickerStore } from '@@/pinia/dash_layout_picker_store'
import { useDashLearnStore } from '@@/pinia/dash_learn_store'
import { useDashMakeStore } from '@@/pinia/dash_make_store'
import { useDashNavigationStore } from '@@/pinia/dash_navigation_store'
import { useDashWallBulkActionsStore } from '@@/pinia/dash_wall_bulk_actions_store'
import { useElectronAppStore } from '@@/pinia/electron_app_store'
import { useExperimentsStore } from '@@/pinia/experiments'
import { ConfirmationDialogLayoutVariant, useGlobalConfirmationDialogStore } from '@@/pinia/global_confirmation_dialog'
import { useGlobalInputDialogStore } from '@@/pinia/global_input_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { LibraryUpgradeStep, useLibraryPlansStore } from '@@/pinia/library_plans'
import { useMagicPadletPanelStore } from '@@/pinia/magic_padlet_panel_store'
import { UpgradeSource, usePersonalPlansStore } from '@@/pinia/personal_plans_store'
import { useUserAccountsStore } from '@@/pinia/user_accounts_store'
import type {
  Folder,
  FreezeBannerState,
  GoogleAppLicensingValidateResponse,
  Library,
  LibraryId,
  PersonalToLibraryTransferWallsResponse,
  PopoverAnchor,
  TenantToLibraryMigrationState,
  User,
  UserOptions,
  WallTemplate,
  WallViz,
} from '@@/types'
import { MobilePage, type DashView } from '@@/types/dash'
import type { JsonAPIResource } from '@padlet/arvo'
import { fetchJson } from '@padlet/fetch'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

interface DashboardStartingState {
  user: User
  userOptions: UserOptions
  brahmsToken: string
  currentUserWallTemplates: WallTemplate[]
  libraryToWallTemplates: Record<LibraryId, WallTemplate[]>
  migrationBannerInfo?: TenantToLibraryMigrationState
  freezeBannerInfo?: FreezeBannerState
  currentCountryCode?: string
}

const useDashStore = defineStore('dashStore', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()
  const dashLayoutPickerStore = useDashLayoutPickerStore()
  const dashMakeStore = useDashMakeStore()
  const personalPlansStore = usePersonalPlansStore()
  const libraryPlansStore = useLibraryPlansStore()
  const dashCollectionsStore = useDashCollectionsStore()
  const globalInputDialogStore = useGlobalInputDialogStore()
  const dashAccountsStore = useDashAccountsStore()
  const magicPadletPanelStore = useMagicPadletPanelStore()
  const electronAppStore = useElectronAppStore()
  const dashNavigationStore = useDashNavigationStore()
  const userAccountsStore = useUserAccountsStore()
  const dashGalleryStore = useDashGalleryStore()
  const dashLearnStore = useDashLearnStore()
  const dashWallBulkActionsStore = useDashWallBulkActionsStore()
  const experimentsStore = useExperimentsStore()
  const classInformationStore = useClassInformationStore()
  const dashAiMakeSuggestionsStore = useDashAiMakeSuggestionsStore()
  // State

  const isInitialized = ref<boolean>(false)
  const isInitializing = ref<boolean>(false)
  const currentMobilePage = ref<MobilePage>(MobilePage.AccountsMenu)
  const xWallsSearchModal = ref<boolean>(false)
  const xGettingStarted = ref<boolean>(false)
  const accountMoreAnchor = ref<PopoverAnchor | null>(null)
  const tenantToLibraryMigrationState = ref<Record<string, any>>()
  const freezeBannerState = ref<FreezeBannerState | null>(null)
  const detectedCountryCode = ref<string>('')
  const xSuggestionsOnMake = ref<boolean>(false)

  // Getters

  const xMigrationBanner = computed(
    (): boolean => tenantToLibraryMigrationState.value != null && !dashAccountsStore.isNativeAccount,
  )
  const migrationProgress = computed((): number => tenantToLibraryMigrationState.value?.completionWeight ?? 0)
  const hasMigrationCompleted = computed((): boolean => migrationProgress.value >= 100)
  const migrationLibraryDashboardUrl = computed(
    (): string => tenantToLibraryMigrationState.value?.libraryDashboardUrl ?? '',
  )

  const xFreezeBanner = computed((): boolean => {
    if (dashAccountsStore.currentLibrary && isAppUsing('schoolLibraryFreeze')) {
      const freezeState = dashAccountsStore.currentLibrary.libraryFreezeState
      const bannerVisibleTo = freezeState?.visibleTo
      const currentUserLibraryMembershipRole = dashAccountsStore.currentLibraryMembership?.role
      if (bannerVisibleTo == null || bannerVisibleTo.length === 0 || currentUserLibraryMembershipRole == null)
        return false

      return bannerVisibleTo.includes(currentUserLibraryMembershipRole)
    } else {
      return !dashAccountsStore.isNativeAccount && freezeBannerState.value?.days_until_freeze !== undefined
    }
  })

  // TODO: PRO-30503 - remove after LTI 1.0 deprecation cutoff (post-cutoff banner ends 2027-01-01)
  const xLtiDeprecationBanner = computed((): boolean => {
    if (dashAccountsStore.currentLibrary?.isLtiV1p0Ready !== true) return false
    if (dashAccountsStore.currentLibrary?.isLtiV1p0BannerHidden === true) return false

    const role = dashAccountsStore.currentLibraryMembership?.role
    if (role !== LibraryMembershipRole.Owner && role !== LibraryMembershipRole.Admin) return false

    const now = Date.now()
    return now >= Date.UTC(2026, 4, 1) && now < Date.UTC(2027, 0, 1)
  })
  const xTenantTransferWallsTool = computed(
    (): boolean => !dashAccountsStore.isNativeAccount && freezeBannerState.value?.days_until_freeze !== undefined,
  )
  const xTenantFrozen = computed(
    (): boolean =>
      !dashAccountsStore.isNativeAccount &&
      freezeBannerState.value?.days_until_freeze != null &&
      freezeBannerState.value?.days_until_freeze <= 0,
  )
  const freezeBannerRole = computed((): string => freezeBannerState.value?.role ?? '')
  const freezeBannerFreeTrial = computed((): boolean => freezeBannerState.value?.free_trial ?? false)
  const freezeBannerDaysUntilFreeze = computed((): number => freezeBannerState.value?.days_until_freeze ?? 0)

  const xGettingStartedRow = computed((): boolean => {
    if (
      dashCollectionsStore.currentAccountKey == null ||
      !dashAccountsStore.isCurrentAccountLibrary ||
      !dashAccountsStore.canCurrentUserOnboardOntoLibrary
    )
      return false

    return dashAccountsStore.isOnboardingForLibrary(dashCollectionsStore.currentAccountKey.id)
  })
  const isCurrentUserPersonalAccountOverQuota = computed((): boolean => {
    if (!dashAccountsStore.isNativeAccount) return false
    if (dashAccountsStore.isCurrentAccountLibrary) return false
    if (dashAccountsStore.currentUser.quota.can_make) return false
    // We only return true if the walls_used > walls_limit since can_make returns false if walls_used === walls_limit
    return dashAccountsStore.currentUser.quota.walls_used > dashAccountsStore.currentUser.quota.walls_limit
  })
  const xOverWallsQuotaWarning = computed((): boolean => {
    if (!isInitialized.value) return false
    // We show the over wall quota warning only for the user's personal collection
    return isCurrentUserPersonalAccountOverQuota.value
  })
  const xTrialExpirationWarning = computed((): boolean => {
    if (!isInitialized.value) return false
    if (!isAppUsing('affiliateSignupLink') && !isAppUsing('selfServeFreeTrial')) return false
    if (!dashAccountsStore.isNativeAccount) return false
    if (dashAccountsStore.isCurrentAccountLibrary) return false
    if (dashAccountsStore.currentUser.membership_tier !== 'trial') return false
    const trialDaysLeft = dashAccountsStore.currentUser.trial_days_left ?? 0
    return trialDaysLeft <= 7 && trialDaysLeft > 0
  })
  const xDesktopQuotaMeter = computed((): boolean => {
    if (!isAppUsing('dashboardUpgradeCta')) return false
    if (dashCollectionsStore.isActiveCollectionShared) return false
    if (dashCollectionsStore.isActiveCollectionBookmark) return false
    if (dashCollectionsStore.isActiveCollectionCrossLibrary) return false
    return true
  })
  const xMobileQuotaMeter = computed((): boolean => {
    if (!isAppUsing('dashboardUpgradeCta')) return false
    if (dashCollectionsStore.isActiveCollectionShared) return false
    if (dashCollectionsStore.isActiveCollectionBookmark) return false
    if (dashCollectionsStore.isActiveCollectionCrossLibrary) return false
    if (
      isActiveFilter(dashCollectionsStore.activeCollectionKey, 'trashed') &&
      currentMobilePage.value === MobilePage.Collection
    )
      return false
    if (
      isActiveFilter(dashCollectionsStore.activeCollectionKey, 'archived') &&
      currentMobilePage.value === MobilePage.Collection
    )
      return false
    return true
  })
  const xLibraryQuotaMeter = computed<boolean>(() => {
    return dashAccountsStore.isCurrentAccountLibrary && dashAccountsStore.isCurrentUserLibraryOwner
  })

  const xPersonalQuotaMeter = computed<boolean>(() => {
    return dashAccountsStore.isNativeAccount && dashAccountsStore.currentAccountKey?.type === 'user'
  })
  const xAccountMore = computed((): boolean => accountMoreAnchor.value != null)
  const defaultDashboardUrl = computed((): string => {
    if (!dashAccountsStore.isNativeAccount) return '/dashboard?mobile_page=AccountsMenu'
    // We return the user's personal dashboard url
    if (dashAccountsStore.defaultLibrary === undefined) return '/dashboard?mobile_page=AccountsMenu'
    const defaultLibraryUrl = transformUrl(dashAccountsStore.defaultLibrary.dashboardUrl, {
      searchParams: {
        mobile_page: MobilePage.Collection,
        filter: 'all',
      },
    })

    // We need to remove the currentHost from the defaultLibraryUrl since
    // getDashViewFromUrlParams handles pathname and not the entire url
    return defaultLibraryUrl.replace(currentHostWithProtocol(), '')
  })

  const isGalleryPage = computed(() => {
    return dashCollectionsStore.activeCollectionKey?.indexKey === 'gallery'
  })

  const xClassInformationDialog = ref<boolean>(false)

  // Teacher school flow takeover (replaces the class information dialog when
  // the school_selection_for_teachers flip is on — the backend guarantees the
  // two starting-state flags are mutually exclusive).
  const xTeacherSchoolFlowScreen = ref<boolean>(false)
  // Google Places key for the school typeahead; null/absent (→ '') when the
  // teacher school flow is not showing (flip off, profile already stored, or
  // after a skip).
  const googleApiKeyForMap = ref<string>('')
  // "new" = freshly onboarded teacher who must complete the flow (back button,
  // no skip); "existing" = pre-existing teacher (skippable, no back).
  const teacherSchoolFlowMode = ref<'new' | 'existing'>('existing')

  // Persistent sidebar nudge shown to a teacher who skipped the takeover and
  // still has no profile. Backend guarantees it is mutually exclusive with
  // showTeacherSchoolFlow.
  const xTeacherSchoolInfoPrompt = ref<boolean>(false)
  // True when the takeover was re-opened from that nudge (rather than the
  // automatic dashboard prompt). Skipping in this case must NOT rewrite the
  // skip timestamp.
  const teacherSchoolFlowOpenedFromInfoPrompt = ref<boolean>(false)

  // Mirrors the xClassInformationDialog read in initializeSharedState; split
  // into an action so the starting-state capture is unit-testable without
  // running the full shared-state initialization chain.
  function initializeTeacherSchoolFlowScreen(): void {
    xTeacherSchoolFlowScreen.value = window.ww.vueStartingState.showTeacherSchoolFlow === true
    googleApiKeyForMap.value = window.ww.vueStartingState.googleApiKeyForMap ?? ''
    teacherSchoolFlowMode.value = window.ww.vueStartingState.teacherSchoolFlowMode === 'new' ? 'new' : 'existing'
    xTeacherSchoolInfoPrompt.value = window.ww.vueStartingState.showTeacherSchoolInfoPrompt === true
  }

  function closeTeacherSchoolFlowScreen(): void {
    xTeacherSchoolFlowScreen.value = false
    teacherSchoolFlowOpenedFromInfoPrompt.value = false
  }

  // Re-open the takeover from the sidebar nudge. Existing mode so the
  // voluntary entry has a soft exit; the origin flag makes that skip a plain
  // close (no skip rewrite. See TeacherSchoolFlowScreen).
  function openTeacherSchoolFlowFromInfoPrompt(): void {
    teacherSchoolFlowOpenedFromInfoPrompt.value = true
    teacherSchoolFlowMode.value = 'existing'
    xTeacherSchoolFlowScreen.value = true
  }

  function hideTeacherSchoolInfoPrompt(): void {
    xTeacherSchoolInfoPrompt.value = false
  }

  function showTeacherSchoolInfoPrompt(): void {
    xTeacherSchoolInfoPrompt.value = true
  }

  async function closeClassInformationDialog(): Promise<void> {
    trackEvent('Onboarding', 'Skipped class information')
    xClassInformationDialog.value = false
    // Only trigger an upsert if the class information is empty
    // This avoids constantly showing the class information dialog if the user doesn't want to fill it out
    if (classInformationStore.classInformation === '') {
      await classInformationStore.upsertClassInformation({
        newClassInfo: '',
      })
    }
  }

  async function submitClassInformation(classInformation: string): Promise<void> {
    trackEvent('Onboarding', 'Submitted class information')
    xClassInformationDialog.value = false
    await classInformationStore.upsertClassInformation({
      newClassInfo: classInformation,
    })
    if (xSuggestionsOnMake.value) {
      void dashAiMakeSuggestionsStore.fetchAiMakeSuggestions()
    }
  }

  /**
   * ==================== INITIALIZE STORES ====================
   */

  function initializePreSelectedWallIdsFromParamsForPermanentlyDelete(): void {
    const filter = getSearchParam('filter')
    const wallIds = getAndClearSearchParam('wallIds')
    if (wallIds != null && filter === 'trashed') {
      const wallIdsArray = wallIds.split(',').map((id) => parseInt(id, 10))
      dashWallBulkActionsStore.preselectedWallIds = wallIdsArray
    }
  }

  /**
   * Initialize essential state that must be set before any navigation.
   * This includes experiments and activeUserId which are required by collections store.
   */
  function initializeEssentialState(): void {
    const currentUserMakePageSimplifiedLayoutExperimentVariant =
      window.ww.vueStartingState.makePageSimplifiedLayoutExperimentVariant
    experimentsStore.setMakePageSimplifiedLayoutExperimentVariant(currentUserMakePageSimplifiedLayoutExperimentVariant)

    dashCollectionsStore.setActiveUserId(dashAccountsStore.currentUser.id)
    setScope({ userId: dashAccountsStore.currentUser.id })
  }

  /**
   * Initialize shared dashboard state (data fetching, snackbars, etc.)
   * Used by both regular dashboard and campus dashboard.
   * Does NOT include navigation initialization - each dashboard handles that separately.
   * NOTE: initializeEssentialState() must be called before this.
   */
  async function initializeSharedState(): Promise<void> {
    initializeTeacherSchoolFlowScreen()

    // fetchFolders, fetchFavoritesWallIds and initialize before switchViewToUrlParams
    const dashboardInitializationPromises = [
      fetchDashStartingState(),
      dashAccountsStore.initialize(),
      dashCollectionsStore.fetchFolders(),
      dashCollectionsStore.fetchFavoritesWallIds(),
    ]
    if (dashAccountsStore.isBackpack) dashboardInitializationPromises.push(dashCollectionsStore.fetchUserGroupFolders())
    if (dashAccountsStore.isCurrentLibrarySchool)
      dashboardInitializationPromises.push(dashCollectionsStore.fetchCurrentLibraryUserGroupFolders())

    await Promise.all(dashboardInitializationPromises)

    // Init gallery store
    void dashGalleryStore.initialize()
    void dashLearnStore.fetchLearnVideosIfNecessary()

    initializePreSelectedWallIdsFromParamsForPermanentlyDelete()

    const currentUserSmartSuggestionsExperimentVariant = window.ww.vueStartingState.smartSuggestionsExperimentVariant
    experimentsStore.setSmartSuggestionsExperimentVariant(currentUserSmartSuggestionsExperimentVariant)

    const currentUserSelfServeFreeTrialExperimentVariant =
      window.ww.vueStartingState.selfServeFreeTrialExperimentVariant
    experimentsStore.setSelfServeFreeTrialExperimentVariant(currentUserSelfServeFreeTrialExperimentVariant)
    const smartSuggestionsEnabled =
      isAppUsing('smartSuggestionsExperiment') &&
      currentUserSmartSuggestionsExperimentVariant === SmartSuggestionsExperimentVariant.Enabled

    // Initialize AI suggestions and class information dialog
    xSuggestionsOnMake.value =
      (smartSuggestionsEnabled || isAppUsing('aiSuggestionsOnMakePage')) &&
      dashAccountsStore.currentUserOptions.isTeacher === true

    xClassInformationDialog.value = window.ww.vueStartingState.showClassInformation === true

    if (
      dashAccountsStore.currentUserOptions.isTeacher === true &&
      !xClassInformationDialog.value &&
      !xTeacherSchoolFlowScreen.value
    ) {
      void classInformationStore.fetchClassInformation()
      if (xSuggestionsOnMake.value) {
        void dashAiMakeSuggestionsStore.fetchAiMakeSuggestions()
      }
    }

    if (dashAccountsStore.currentUser.isEmailRecentlyLoggedInAndVerified as boolean) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Email verified'),
      })
    }

    if (window.ww.vueStartingState.trashedWallFromSurface === true) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: __('Moved to trash'),
        timeout: 5000,
      })
    }

    if (window.ww.vueStartingState.showUpgradeModal === true) {
      personalPlansStore.userTriggeredUpgrade({
        user: dashAccountsStore.currentUser,
        upgradeSource: UpgradeSource.WallQuota,
      })
    }

    // Display snackbar if displaySnackbarMessage is not null and not undefined
    if (window.ww.vueStartingState.displaySnackbarMessage != null) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.success,
        message: decodeURIComponent(window.ww.vueStartingState.displaySnackbarMessage as string),
        timeout: 5000,
      })
    }

    // Display error snackbar if displayErrorSnackbarMessage is not null and not undefined
    if (window.ww.vueStartingState.displayErrorSnackbarMessage != null) {
      globalSnackbarStore.setSnackbar({
        notificationType: SnackbarNotificationType.error,
        message: window.ww.vueStartingState.displayErrorSnackbarMessage as string,
        timeout: 5000,
      })
    }

    if (window.ww.vueStartingState.showLibraryWallTransferModal === true) {
      const paragraph1 = __('You can now create and share so much more in your new school workspace!')
      const paragraph2 = __(
        '<strong>We highly recommend transferring any personal padlets into your school</strong> so everything is easy to find in one place. If you′d prefer to transfer later, you can find a guide <a target="_blank" class="underline text-dark-text-100 dark:text-light-text-100" href="%{helpArticle}">here</a>.',
        {
          helpArticle:
            'https://padlet.help/article/179fiy69db-how-can-i-import-my-padlets-from-to-a-padlet-for-schools-account-from-an-existing-account#import_to_a_padlet_for_schools_account_at_padlet_com',
        },
      )

      useGlobalConfirmationDialogStore().openConfirmationDialog({
        ...WAVING_HAND_EMOJI,
        title: __('Welcome to %{SchoolName}', {
          SchoolName: dashAccountsStore.currentLibrary?.name ?? 'your school',
        }),
        body: `<p class="mb-6">${paragraph1}</p>
          <p class="mb-6">${paragraph2}</p>`,
        confirmButtonText: __('Transfer my padlets'),
        cancelButtonText: __('Not now'),
        afterConfirmCallbacks: [
          async () => {
            if (dashAccountsStore.currentLibrary?.id == null) return

            try {
              const response = await PersonalToLibraryTransferWallsApi.transferWalls(
                dashAccountsStore.currentLibrary.id,
              )
              const data = (response.data as JsonAPIResource<PersonalToLibraryTransferWallsResponse>).attributes
              if (data.completed) {
                globalSnackbarStore.setSnackbar({
                  notificationType: SnackbarNotificationType.success,
                  message: __('Padlets transferred'),
                })
              } else {
                globalSnackbarStore.setSnackbar({
                  notificationType: SnackbarNotificationType.success,
                  message: __('Transfer in progress, this should only take a minute...'),
                })
              }
            } catch (e) {
              globalSnackbarStore.setSnackbar({
                notificationType: SnackbarNotificationType.error,
                message: __('Failed to transfer padlets'),
              })
              captureFetchException(e, { source: 'PersonalToLibraryTransferWalls' })
            }
          },
        ],
        afterCancelActions: [
          async () => {
            try {
              await PersonalToLibraryTransferWallsApi.markTransferAsSeen()
            } catch (e) {
              captureFetchException(e, { source: 'PersonalToLibraryTransferWalls' })
            }
          },
        ],
        forceFullWidthButtons: false,
        layoutVariant: ConfirmationDialogLayoutVariant.Large,
      })
    }

    void dashCollectionsStore.fetchSharedCollections()

    // Initialize the electron app store
    electronAppStore.setCurrentUser(dashAccountsStore.currentUser)

    // Eagerly fetch data
    void personalPlansStore.initialize()
    void dashAccountsStore.fetchWallViewableLibraries()
    void dashAccountsStore.fetchWallCreatableLibraries()
    if (dashAccountsStore.isNativeAccount) void dashAccountsStore.fetchUserLibraryMemberships()
    void dashCollectionsStore.fetchAllSchoolUserGroupFolders()
    void dashLearnStore.fetchLearnVideosIfNecessary()
    if (isAppUsing('googleAppLicensing')) {
      void validateGoogleAppLicense()
    }

    experimentsStore.setUpgradeModalCopyAExperimentVariant(
      window.ww.vueStartingState.upgradeModalCopyAExperimentVariant,
    )
    experimentsStore.setUpgradeModalSingleStepExperimentVariant(
      window.ww.vueStartingState.upgradeModalSingleStepExperimentVariant,
    )
  }

  /**
   * Initialize the regular dashboard (non-campus).
   * Calls shared initialization plus navigation initialization.
   */
  async function initialize(): Promise<void> {
    // Defer wall fetching: both navigation switches below route through switchView, which would
    // otherwise each restart pagination from page 0. We fetch once at the end instead.
    isInitializing.value = true
    try {
      // Essential state must be set before any navigation (activeUserId, experiments)
      initializeEssentialState()

      // Update the current url to the default library
      dashNavigationStore.updateCurrentUrlToDefaultLibrary()

      // Early switch tab to show the correct page
      dashNavigationStore.switchTabAndViewFromUrlParams()

      await initializeSharedState()

      dashNavigationStore.switchToInitialTabAndView()
    } finally {
      isInitializing.value = false
    }

    void dashCollectionsStore.fetchActiveCollectionWallsNow()

    // Reveal dashboard items after all navigation is complete
    isInitialized.value = true
  }

  const fetchDashStartingStateStatus = ref(FetchJsonStatus.Fetching)
  const isFetchingHomePageData = computed(() => {
    // TODO: remove this check after releasing dashboard caching publicly
    if (isInitialized.value) return false
    return (
      fetchDashStartingStateStatus.value === FetchJsonStatus.Fetching ||
      userAccountsStore.isFetchingAccounts ||
      dashCollectionsStore.isFetchingFolders
    )
  })

  async function fetchDashStartingState(): Promise<void> {
    try {
      await fetchCachedQuery<DashboardStartingState>({
        cacheKey: ['fetchDashStartingState', buildUserIdCacheKey(dashAccountsStore.currentUser.id)],
        queryFn: async () => await fetchJson('/api/1/dashboard/starting-state'),
        onFetchSuccess: (response) => {
          const {
            userOptions,
            brahmsToken,
            currentUserWallTemplates,
            libraryToWallTemplates,
            migrationBannerInfo,
            freezeBannerInfo,
            currentCountryCode,
          } = response
          dashAccountsStore.updateUserOptions({ userOptions })
          electronAppStore.setBrahmsToken(brahmsToken)
          void dashLayoutPickerStore.initializeState({
            currentUserWallTemplates,
            libraryToWallTemplates,
          })
          void dashMakeStore.initialize({
            currentUserWallTemplates,
            libraryToWallTemplates,
          })
          if (migrationBannerInfo != null) tenantToLibraryMigrationState.value = migrationBannerInfo
          if (freezeBannerInfo != null) freezeBannerState.value = freezeBannerInfo
          if (currentCountryCode != null) detectedCountryCode.value = currentCountryCode
          fetchDashStartingStateStatus.value = FetchJsonStatus.Completed
        },
      })
    } catch (e) {
      captureFetchException(e, { source: 'DashFetchDashStartingState' })
      fetchDashStartingStateStatus.value = FetchJsonStatus.Error
    }
  }

  async function validateGoogleAppLicense(): Promise<void> {
    try {
      const validationResponse = await GoogleAppLicenseApi.checkLicense()
      const licenseStatus = (validationResponse?.data as JsonAPIResource<GoogleAppLicensingValidateResponse>).attributes
      if (!licenseStatus.invalidLicense) return
      navigateTo(licenseStatus.nextUrl as string, { method: 'post' })
    } catch (error) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(error, { source: 'DashValidateGoogleAppLicense' })
    }
  }

  /**
   * ==================== NAVIGATION ====================
   */

  function updatePageTitleAndFocusOnH1(): void {
    const prevPageTitle = window.document.title
    const focusOnH1Interval = setInterval(() => {
      const h1Element = document.querySelector('h1')
      if (h1Element == null) return

      const h1ElementTextContent = h1Element.textContent?.trim()
      if (h1ElementTextContent != null && prevPageTitle !== h1ElementTextContent) {
        window.document.title = h1ElementTextContent

        // if user is still on the menu item button that causes the view to change,
        // we assume they're waiting for the page to load, so we focus on the h1 element once it's loaded
        if (document.activeElement?.textContent?.trim() === h1ElementTextContent) {
          h1Element.focus()
        }
        clearInterval(focusOnH1Interval)
      }
    }, 100)
  }
  function switchView(dashView: DashView): void {
    // We save the tab the user is currently in before switching view
    saveDashViewUrlIntoStorage()

    // Special cases
    const page = dashView.page
    const gallery = dashView.gallery
    // filter is for convenience
    const filter = dashView.filter
    const collectionKey =
      dashView.collectionKey ?? (filter !== undefined ? { typeKey: 'filter', indexKey: filter } : null)
    const accountKey = dashView.accountKey
    const mobilePage = dashView.mobilePage
    const sharedCollectionAccountKey = dashView.sharedCollectionAccountKey
    // Track dashboard page views
    const filterInSentenceCase = (filter: string): string =>
      (filter.charAt(0).toUpperCase() + filter.slice(1)).replace(/_/g, ' ')
    if (filter !== undefined) {
      trackPageview('Dashboard', filterInSentenceCase(filter))
    } else if (gallery === 'all') {
      trackPageview('Dashboard', 'Gallery')
    }

    // SPECIAL CASE: getting started
    if (
      page === MobilePage.GettingStarted ||
      // Auto-detect
      (accountKey?.type === 'library' &&
        filter === undefined &&
        collectionKey == null &&
        mobilePage === undefined &&
        dashAccountsStore.isOnboardingForLibrary(accountKey?.id))
    ) {
      if (accountKey != null) {
        dashCollectionsStore.switchAccountKey(accountKey)
      }

      xGettingStarted.value = true
      currentMobilePage.value = MobilePage.GettingStarted
      return
    } else {
      if (xGettingStarted.value) {
        xGettingStarted.value = false
        currentMobilePage.value = MobilePage.AccountsMenu
      }
    }

    // SPECIAL CASE: Layout picker
    if (page === 'layoutPicker') {
      // We switch accountKey here else the accountKey will default to personal library in the layout picker
      if (accountKey !== undefined) {
        dashCollectionsStore.switchAccountKey(accountKey)
      }
      dashLayoutPickerStore.openLayoutPicker()
      currentMobilePage.value = MobilePage.LayoutPicker
      return
    }

    // View the correct page on mobile
    if (mobilePage !== undefined) {
      currentMobilePage.value = mobilePage
    }

    // Set the account-collection pair on the collections store
    if (accountKey != null && collectionKey != null) {
      // If both are set, we assume that it's a valid account-collection pair.
      dashCollectionsStore.setAccountCollectionCursor({ accountKey, collectionKey })
    } else if (accountKey != null) {
      // Automatically switches to valid collectionKey where possible.
      dashCollectionsStore.switchAccountKey(accountKey)
    } else if (collectionKey != null) {
      // Automatically switches to valid accountKey where possible.
      dashCollectionsStore.switchCollectionKey(collectionKey)
    }

    if (gallery !== undefined) {
      dashCollectionsStore.switchGalleryViz(gallery as WallViz)
    }

    // Special case shared collection
    if (
      (collectionKey?.indexKey === 'combined_shared_user' || collectionKey?.indexKey === 'combined_shared_library') &&
      !(sharedCollectionAccountKey == null)
    ) {
      dashCollectionsStore.viewSharedCollection({
        collectionKey,
        sharedCollectionAccountKey,
      })
      return
    }

    // "View" the collection. Skipped during initialize(), which fetches once after the final view is set.
    if (
      !isInitializing.value &&
      (accountKey != null || collectionKey != null || gallery !== undefined || mobilePage === MobilePage.Collection)
    ) {
      void dashCollectionsStore.fetchActiveCollectionWallsNow()
    }

    updatePageTitleAndFocusOnH1()
  }

  function switchViewToUrlParams(): void {
    let dashView: DashView = getDashViewFromUrlParams()
    const dashViewUrlFromStorage = getDashViewUrlFromStorage()

    const isLibraryHidden = (): boolean => {
      // exit early if there are no relevant library slug data in dashView
      if (dashView.librarySlug === undefined) return false

      const libraryId = dashAccountsStore.wallViewableAndVisibleLibrariesArray.find(
        (library: Library) => library.slug === dashView.librarySlug,
      )?.id
      if (libraryId !== undefined) {
        // Update the dashView with the current libraryId to display it on the dashboard
        dashView = { ...dashView, accountKey: { type: 'library', id: libraryId } }
        return false
      }

      return true
    }

    const isAccessingHiddenPersonalAccountView = (): boolean => {
      return dashView.librarySlug === undefined && dashAccountsStore.currentUser.visible === false
    }

    // Load last opened view URL if visiting the dashboard without params
    if (isEmpty(dashView) && dashViewUrlFromStorage !== null) {
      window.history.pushState(null, '', dashViewUrlFromStorage)
      dashView = getDashViewFromUrlParams()
      if (isLibraryHidden() || isAccessingHiddenPersonalAccountView()) {
        switchToRecentsOrDefaultLibraryView()
        return
      }
    }

    if (isEmpty(dashView)) {
      switchToRecentsOrDefaultLibraryView()
      return
    }

    // We check if the mobile_page has been altered by the user
    const isMobilePageInvalid = (): boolean => {
      if (dashView.mobilePage === undefined) return false
      return MobilePage[dashView.mobilePage as string] === undefined
    }

    // Assumes that accounts and onboarding data has been fetched prior
    const isLibrarySlugInvalid = (): boolean => {
      // exit early if there are no relevant library slug data in dashView
      if (dashView.librarySlug === undefined) return false

      const libraryId = dashAccountsStore.librariesArray.find(
        (library: Library) => library.slug === dashView.librarySlug,
      )?.id
      if (libraryId !== undefined) {
        // Update the dashView with the current libraryId to display it on the dashboard
        dashView = { ...dashView, accountKey: { type: 'library', id: libraryId } }
        return false
      }

      return true
    }

    // Assumes that folder data has been fetched prior
    const isFolderIdInvalid = (): boolean => {
      // exit early if there are no relevant folder data in dashView
      const collectionKey = dashView.collectionKey
      if (collectionKey == null || collectionKey.typeKey !== 'folderId') return false
      if (isNaN(collectionKey.indexKey)) return true

      return !dashCollectionsStore.foldersArray.some((folder: Folder) => folder.id === collectionKey.indexKey)
    }

    const isNavigatedFromDashboardSettings = (): boolean => {
      const dashboardSettingsUrl = buildUrlFromPath('dashboard/settings')
      return document.referrer.startsWith(dashboardSettingsUrl)
    }

    // If no valid mobile page exists or library slug exists for the slug or if folder id is invalid
    // we redirect the user to either recents or user > all
    if (
      isMobilePageInvalid() ||
      isLibrarySlugInvalid() ||
      isFolderIdInvalid() ||
      // We still allow people to visit a hidden library dashboard via URL or redirected from other pages
      // There is a case when the user is on a library dashboard -> go to /dashboard/settings/dashboard -> hide the library -> go back to dashboard
      // The app should display Recents or the default library view instead of the hidden library view
      (isLibraryHidden() && isNavigatedFromDashboardSettings())
    ) {
      switchToRecentsOrDefaultLibraryView()
      return
    }

    const isMobilePageNavATabView = (): boolean => {
      const mobilePageNavTabs = [
        MobilePage.AccountsMenu,
        MobilePage.Search,
        MobilePage.LayoutPicker,
        MobilePage.Join,
        MobilePage.More,
      ]
      return dashView.mobilePage !== undefined && mobilePageNavTabs.includes(dashView.mobilePage)
    }

    // Ensure that the layout picker will display the default library on the first render on mobile view
    // Context: When you first visit a tab on the mobile nav bar, then open the layout picker, the default library should be selected. See the details in the method setInitialLibrarySelected in app/javascript/pinia/dash_layout_picker_store.ts
    if (isMobilePageNavATabView() && dashAccountsStore.defaultLibrary !== undefined) {
      dashView = { ...dashView, accountKey: { type: 'library', id: dashAccountsStore.defaultLibrary.id } }
    }

    switchView(dashView)

    // Switch to "recents" or "default library" if current account cannot see "archived".
    // Note that this needs to be after switchView due to before switchView call we set the accountKey to be `user` instead of `library`.
    if (!dashAccountsStore.canCurrentAccountArchiveWall && dashView.filter === 'archived') {
      switchToRecentsOrDefaultLibraryView()
    }
  }

  function switchToRecentsOrDefaultLibraryView(): void {
    if (dashAccountsStore.user.hasRecentPadlets === true) {
      window?.history?.pushState(
        null,
        '',
        dashViewUrl({ mobilePage: MobilePage.Collection, filter: 'combined_recents' }, { path: '/dashboard' }),
      )
      const dashView = getDashViewFromUrlParams()
      switchView(dashView)
    } else {
      window?.history?.pushState(null, '', defaultDashboardUrl.value)
      let dashView = getDashViewFromUrlParams()

      if (dashAccountsStore.isNativeAccount && dashAccountsStore.defaultLibrary !== undefined) {
        // We need to update the accountKey since it is the native user accountKey by default
        dashView = { ...dashView, accountKey: { type: 'library', id: dashAccountsStore.defaultLibrary.id } }
      }
      switchView(dashView)
    }
  }

  /**
   * ==================== JOIN PADLET ====================
   */

  function promptURLForJoinPadlet(payload: { source: string }): void {
    globalInputDialogStore.openInputDialog({
      title: __('Enter URL'),
      shouldFadeIn: true,
      inputPlaceholder: 'https://padlet.com/gallery/burgers',
      inputAriaLabel: __('Enter the padlet URL'),
      submitActions: [({ inputValue }) => joinPadletURLAction({ inputValue, source: payload.source })],
      validationActions: [checkJoinPadletUrlValidity],
    })
  }

  function joinPadletURLAction({ inputValue, source }: { inputValue: string; source: string }): void {
    if (source === 'join_tab') {
      trackEvent('Dashboard', 'Joined padlet from join tab')
    } else if (source === 'join_page') {
      trackEvent('Dashboard', 'Joined padlet from join page')
    }
    navigateTo(inputValue, { target: '_blank' })
  }

  function checkJoinPadletUrlValidity({ inputValue }: { inputValue: string }): void {
    const isValidUrl =
      isValidWallUrlFormat(inputValue) || isBreakoutUrlFormat(inputValue) || isSubmissionRequestUrlFormat(inputValue)
    globalInputDialogStore.setSubmitButtonDisable(!isValidUrl)
  }

  async function createWallAndRefetchData(payload: { layout: string; groupBySection?: boolean }): Promise<void> {
    await dashLayoutPickerStore.createWall({ viz: payload.layout, groupBySection: payload.groupBySection })

    if (device.electronApp) {
      void dashCollectionsStore.fetchActiveCollectionWallsNow()
      dashAccountsStore.refreshWallQuotas()
      void personalPlansStore.initialize()
    }
  }

  /**
   * ==================== LAYOUT PICKER ====================
   */

  function closeLayoutPicker(): void {
    dashLayoutPickerStore.closeLayoutPicker()
    currentMobilePage.value = MobilePage.CollectionsMenu
  }

  function closeLayoutPickerAndUpgrade(payload: { user: User | null; librarySelected: Library | null }): void {
    closeLayoutPicker()
    handleUpgrade(payload)
  }

  function handleUpgrade({ user, librarySelected }: { user: User | null; librarySelected: Library | null }): void {
    // Native user
    if (librarySelected == null && user != null) {
      void personalPlansStore.quotaTriggeredUpgrade({ user })
      return
    }

    // Library user
    if (librarySelected != null && librarySelected.libraryType === LibraryType.Classroom) {
      // Classroom libraries only have an annual plan, so when classroom owners upgrade to Gold on the dashboard (the account menu and the layout picker)
      // the app should direct them to the checkout page.
      void libraryPlansStore.fetchPlansAndNavigateToCheckoutUrl({
        libraryId: librarySelected.id,
        libraryType: LibraryType.Classroom,
      })
    } else if (librarySelected != null) {
      void libraryPlansStore.fetchPlansAndShowUpgradeModal({
        upgradeStep: LibraryUpgradeStep.ChooseTierPadletQuota,
        library: librarySelected,
      })
    }
  }

  function closeLayoutPickerAndOpenMagicPadletPanel(): void {
    closeLayoutPicker()
    magicPadletPanelStore.openMagicPadletPanel()
  }

  /**
   * ==================== SEARCH ====================
   */

  function closeWallsSearchModal(): void {
    xWallsSearchModal.value = false
    dashCollectionsStore.setActiveSearchQuery({ activeSearchQuery: '' })
  }

  function openWallsSearchModal(): void {
    xWallsSearchModal.value = true
  }

  /**
   * ==================== MORE MENU ====================
   */
  function hideAccountMore(): void {
    accountMoreAnchor.value = null
  }

  function showAccountMore(payload: { popoverAnchor: PopoverAnchor }): void {
    updateAccountMoreAnchor({ popoverAnchor: payload.popoverAnchor })
  }

  function updateAccountMoreAnchor(payload: { popoverAnchor: PopoverAnchor }): void {
    accountMoreAnchor.value = payload.popoverAnchor
  }

  return {
    // State
    detectedCountryCode,

    // Getters
    xMigrationBanner,
    migrationProgress,
    hasMigrationCompleted,
    migrationLibraryDashboardUrl,
    xFreezeBanner,
    xTenantTransferWallsTool,
    xTenantFrozen,
    freezeBannerRole,
    freezeBannerFreeTrial,
    freezeBannerDaysUntilFreeze,
    xGettingStarted,
    xGettingStartedRow,
    currentMobilePage,
    isCurrentUserPersonalAccountOverQuota,
    xOverWallsQuotaWarning,
    xTrialExpirationWarning,
    xDesktopQuotaMeter,
    xMobileQuotaMeter,
    xLibraryQuotaMeter,
    xPersonalQuotaMeter,
    isInitialized,
    isInitializing,
    xAccountMore,
    accountMoreAnchor,
    xWallsSearchModal,
    isGalleryPage,
    isFetchingHomePageData,
    xSuggestionsOnMake,
    xLtiDeprecationBanner,

    /**
     * ==================== INITIALIZE STORES ====================
     */

    initialize,
    initializeEssentialState,
    initializeSharedState,
    validateGoogleAppLicense,

    /**
     * ==================== NAVIGATION ====================
     */

    switchView,
    switchViewToUrlParams,
    switchToRecentsOrDefaultLibraryView,

    /**
     * ==================== JOIN PADLET ====================
     */

    promptURLForJoinPadlet,
    createWallAndRefetchData,

    /**
     * ==================== LAYOUT PICKER ====================
     */
    closeLayoutPicker,
    closeLayoutPickerAndUpgrade,
    handleUpgrade,
    closeLayoutPickerAndOpenMagicPadletPanel,

    /**
     * ==================== SEARCH ====================
     */

    closeWallsSearchModal,
    openWallsSearchModal,

    /**
     * ==================== MORE MENU ====================
     */

    hideAccountMore,
    showAccountMore,
    updateAccountMoreAnchor,

    /**
     * ==================== CLASS INFORMATION ====================
     */
    xClassInformationDialog,
    closeClassInformationDialog,
    submitClassInformation,

    /**
     * ==================== TEACHER SCHOOL FLOW ====================
     */
    xTeacherSchoolFlowScreen,
    googleApiKeyForMap,
    teacherSchoolFlowMode,
    initializeTeacherSchoolFlowScreen,
    closeTeacherSchoolFlowScreen,
    xTeacherSchoolInfoPrompt,
    teacherSchoolFlowOpenedFromInfoPrompt,
    openTeacherSchoolFlowFromInfoPrompt,
    hideTeacherSchoolInfoPrompt,
    showTeacherSchoolInfoPrompt,
  }
})
export { useDashStore }
